'use client';

import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import type { Story } from '@/contexts/stories/types';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { StoryStatus } from '../types';
import { formatAgeGroup } from '../utils/formatAgeGroup';
import { Edit2, Trash2, Headphones, BookOpen, Play, Pause, Heart, Loader2 } from 'lucide-react';
import { useRunningOrderStories } from '@/contexts/pipeline-v2/hooks/useOrderLayers';
import { useStoryMutations } from '../hooks/useStoryMutations';
import { useToggleStoryFavorite } from '../hooks/useToggleStoryFavorite';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { useToast } from '@/shared/components/ui/use-toast';

interface StoryTableViewProps {
  stories: Story[];
  locale: string;
  onDelete: (storyId: string) => void;
  audioCountByStory?: Record<string, number>;
  bookCountByStory?: Record<string, number>;
  firstAudioUrlByStory?: Record<string, string>;
}

const STATUS_COLORS: Record<StoryStatus, string> = {
  [StoryStatus.Draft]: 'bg-muted text-muted-foreground',
  [StoryStatus.GeneratingText]: 'bg-primary/10 text-primary',
  [StoryStatus.TextCompleted]: 'bg-success/10 text-success',
  [StoryStatus.TextEdited]: 'bg-secondary text-secondary-foreground',
  [StoryStatus.ReadyForAudio]: 'bg-success/20 text-success',
  [StoryStatus.GeneratingAudio]: 'bg-primary/20 text-primary',
  [StoryStatus.AudioCompleted]: 'bg-success/30 text-success',
  [StoryStatus.Assembling]: 'bg-warning/10 text-warning-foreground dark:text-warning',
  [StoryStatus.Completed]: 'bg-success/10 text-success',
  [StoryStatus.Error]: 'bg-destructive/10 text-destructive',
};

export function StoryTableView({
  stories,
  locale,
  onDelete,
  audioCountByStory = {},
  bookCountByStory = {},
  firstAudioUrlByStory = {},
}: StoryTableViewProps) {
  const router = useRouter();
  const t = useTranslations('stories.table');
  const tCard = useTranslations('stories.card');
  const tCreate = useTranslations('stories.create');
  const tFav = useTranslations('stories.favorites');
  const intlLocale = useLocale();
  const dateLocaleTag = ({ ru: 'ru-RU', en: 'en-US', cs: 'cs-CZ', uk: 'uk-UA' } as Record<string, string>)[intlLocale] ?? intlLocale;
  const { toast } = useToast();
  const [deleteStoryId, setDeleteStoryId] = React.useState<string | null>(null);
  const [playingStoryId, setPlayingStoryId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // v2 orders still running → «Создаётся…» on their rows (SSE layer events refresh it)
  const { data: runningIds = [] } = useRunningOrderStories(stories.length > 0);
  const running = useMemo(() => new Set(runningIds), [runningIds]);
  const generatingBadge = (storyId: string) => running.has(storyId) && (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium ${STATUS_COLORS[StoryStatus.GeneratingText]}`}
      data-testid={`story-generating-badge-${storyId}`}
    >
      <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
      {tCard('status.generating_text')}
    </span>
  );

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { audioRef.current?.pause(); };
  }, []);

  const handleQuickPlay = useCallback((e: React.MouseEvent, storyId: string) => {
    e.stopPropagation();
    const url = firstAudioUrlByStory[storyId];
    if (!url) return;

    if (playingStoryId === storyId) {
      audioRef.current?.pause();
      setPlayingStoryId(null);
      return;
    }

    audioRef.current?.pause();
    const audio = new Audio(mediaSrc(url));
    audioRef.current = audio;
    audio.onended = () => setPlayingStoryId(null);
    audio.onerror = () => setPlayingStoryId(null);
    audio.play();
    setPlayingStoryId(storyId);
  }, [playingStoryId, firstAudioUrlByStory]);

  // Server action mutation for story deletion
  const { deleteStory } = useStoryMutations(null);

  // Favorite toggle mutation
  const toggleFavorite = useToggleStoryFavorite();

  // Sort stories by creation date (newest first)
  const sortedStories = useMemo(() => {
    return [...stories].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [stories]);

  const handleDeleteClick = (e: React.MouseEvent, storyId: string) => {
    e.stopPropagation();
    setDeleteStoryId(storyId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteStoryId) return;

    try {
      const result = await deleteStory.mutateAsync(deleteStoryId);

      if (!result.success) {
        throw new Error(result.error);
      }

      onDelete(deleteStoryId);
      setDeleteStoryId(null);
    } catch (error) {
      console.error('Error deleting story:', error);
      toast({ title: tCard('deleteError'), variant: 'destructive' });
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(dateLocaleTag);
  };

  const truncateText = (text: string, maxLen = 100) => {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen).trimEnd() + '…';
  };

  return (
    <div data-testid="story-table">
      {/* Mobile: Card-style list (< 768px) */}
      <div className="md:hidden space-y-3">
        {sortedStories.map((story) => {
          return (
            <div
              key={story.id}
              data-testid={`story-row-${story.id}`}
              onClick={() => router.push(`/${locale}/stories/${story.id}`)}
              className="bg-card rounded-lg border border-border p-4 cursor-pointer hover:border-primary/50 hover:shadow-md active:shadow-sm transition-all"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-foreground line-clamp-2 mb-2">{story.title}</h3>

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mb-3">
                  {generatingBadge(story.id)}
                  {story.storyType && tCreate.has(`storyTypes.${story.storyType}`) && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium">
                      {tCreate(`storyTypes.${story.storyType}` as any)}
                    </span>
                  )}
                  <span>{formatDate(story.createdAt)}</span>
                  {story.ageGroup && (
                    <span>{tCard('ageYears', { age: formatAgeGroup(story.ageGroup) })}</span>
                  )}
                  {story.textBlocks && story.textBlocks.length > 0 && (
                    <span>{story.textBlocks.length} {tCard('blocks')}</span>
                  )}
                  {!!audioCountByStory[story.id] && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-brand-purple/10 text-brand-purple text-xs font-medium" title={tCard('audioCount', { count: audioCountByStory[story.id] })}>
                      <Headphones className="w-3 h-3" />×{audioCountByStory[story.id]}
                    </span>
                  )}
                  {!!bookCountByStory[story.id] && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-brand-orange/10 text-brand-orange text-xs font-medium" title={tCard('bookCount', { count: bookCountByStory[story.id] })}>
                      <BookOpen className="w-3 h-3" />×{bookCountByStory[story.id]}
                    </span>
                  )}
                </div>

                {/* Text excerpt */}
                {story.textBlocks?.[0]?.text && (
                  <p className="text-xs text-muted-foreground line-clamp-2 mb-3">
                    {truncateText(story.textBlocks[0].text)}
                  </p>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFavorite.mutate(story.id); }}
                    disabled={toggleFavorite.isPending}
                    className={`p-2.5 min-touch flex items-center justify-center transition-colors rounded-lg touch-manipulation ${story.isFavorite ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                    title={story.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
                    aria-label={story.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
                    data-testid={`story-favorite-toggle-${story.id}`}
                  >
                    <Heart className={`w-4 h-4 ${story.isFavorite ? 'fill-current' : ''}`} />
                  </button>
                  <button
                    onClick={(e) => handleQuickPlay(e, story.id)}
                    disabled={!firstAudioUrlByStory[story.id]}
                    className="p-2.5 min-touch flex items-center justify-center text-muted-foreground hover:text-primary active:bg-muted transition-colors rounded-lg touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed"
                    title={firstAudioUrlByStory[story.id] ? t('play') : tCard('noAudioYet')}
                    aria-label={t('play')}
                  >
                    {playingStoryId === story.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // The hub edits in place; ?mode=edit was never read by the page.
                      router.push(`/${locale}/stories/${story.id}`);
                    }}
                    className="p-2.5 min-touch flex items-center justify-center text-muted-foreground hover:text-primary active:bg-muted transition-colors rounded-lg touch-manipulation"
                    title={t('edit')}
                    aria-label={t('edit')}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, story.id)}
                    data-testid={`story-delete-${story.id}`}
                    className="p-2.5 min-touch flex items-center justify-center text-muted-foreground hover:text-destructive active:bg-muted transition-colors rounded-lg touch-manipulation"
                    title={t('delete')}
                    aria-label={t('delete')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop: Table view (>= 768px) */}
      <div className="hidden md:block bg-card rounded-lg border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('title')}
                </th>
                <th className="hidden md:table-cell px-4 py-3 bg-muted text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('date')}
                </th>
                <th className="w-20 px-2 py-3 bg-muted text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sortedStories.map((story) => {
                return (
                  <tr
                    key={story.id}
                    data-testid={`story-row-${story.id}`}
                    onClick={() => router.push(`/${locale}/stories/${story.id}`)}
                    className="cursor-pointer hover:bg-muted active:bg-muted/80 transition-colors"
                  >
                    <td className="px-4 py-3 min-touch">
                      <div className="flex flex-col">
                        <span className="font-medium text-foreground line-clamp-2 sm:line-clamp-1">{story.title}</span>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
                          {generatingBadge(story.id)}
                          {story.storyType && tCreate.has(`storyTypes.${story.storyType}`) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-xs font-medium">
                              {tCreate(`storyTypes.${story.storyType}` as any)}
                            </span>
                          )}
                          {story.ageGroup && (
                            <span className="text-xs text-muted-foreground">
                              {tCard('ageYears', { age: formatAgeGroup(story.ageGroup) })}
                            </span>
                          )}
                          {story.textBlocks && story.textBlocks.length > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {story.textBlocks.length} {tCard('blocks')}
                            </span>
                          )}
                          {!!audioCountByStory[story.id] && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-brand-purple/10 text-brand-purple text-xs font-medium" title={tCard('audioCount', { count: audioCountByStory[story.id] })}>
                              <Headphones className="w-3 h-3" />×{audioCountByStory[story.id]}
                            </span>
                          )}
                          {!!bookCountByStory[story.id] && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-brand-orange/10 text-brand-orange text-xs font-medium" title={tCard('bookCount', { count: bookCountByStory[story.id] })}>
                              <BookOpen className="w-3 h-3" />×{bookCountByStory[story.id]}
                            </span>
                          )}
                          {/* Show date on mobile (hidden on md+) */}
                          <span className="md:hidden text-xs text-muted-foreground">
                            {formatDate(story.createdAt)}
                          </span>
                        </div>
                        {/* Text excerpt */}
                        {story.textBlocks?.[0]?.text && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                            {truncateText(story.textBlocks[0].text)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 min-touch">
                      <span className="text-sm text-muted-foreground">{formatDate(story.createdAt)}</span>
                    </td>
                    <td className="px-2 py-3 text-right min-touch">
                      <div className="flex items-center justify-end gap-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleFavorite.mutate(story.id); }}
                          disabled={toggleFavorite.isPending}
                          className={`p-2 min-touch flex items-center justify-center transition-colors rounded touch-manipulation ${story.isFavorite ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                          title={story.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
                          aria-label={story.isFavorite ? tFav('removeFromFavorites') : tFav('addToFavorites')}
                          data-testid={`story-favorite-toggle-${story.id}`}
                        >
                          <Heart className={`w-4 h-4 ${story.isFavorite ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          onClick={(e) => handleQuickPlay(e, story.id)}
                          disabled={!firstAudioUrlByStory[story.id]}
                          className="p-2 min-touch flex items-center justify-center text-muted-foreground hover:text-primary active:bg-muted transition-colors rounded touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed"
                          title={firstAudioUrlByStory[story.id] ? t('play') : tCard('noAudioYet')}
                          aria-label={t('play')}
                        >
                          {playingStoryId === story.id ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // The hub edits in place; ?mode=edit was never read by the page.
                      router.push(`/${locale}/stories/${story.id}`);
                          }}
                          className="p-2 min-touch flex items-center justify-center text-muted-foreground hover:text-primary active:bg-muted transition-colors rounded touch-manipulation"
                          title={t('edit')}
                          aria-label={t('edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => handleDeleteClick(e, story.id)}
                          data-testid={`story-delete-${story.id}`}
                          className="p-2 min-touch flex items-center justify-center text-muted-foreground hover:text-destructive active:bg-muted transition-colors rounded touch-manipulation"
                          title={t('delete')}
                          aria-label={t('delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteStoryId} onOpenChange={() => setDeleteStoryId(null)}>
        <AlertDialogContent data-testid="story-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{tCard('deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {tCard('deleteConfirm.description', {
                title: stories.find((s) => s.id === deleteStoryId)?.title || '',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="story-delete-cancel">{tCard('deleteConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="story-delete-confirm"
              onClick={handleDeleteConfirm}
              disabled={deleteStory.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteStory.isPending ? tCard('deleteConfirm.confirming') : tCard('deleteConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
